/**
 * Vue 3 Meta Pixel Plugin
 * Provides Facebook Pixel tracking functionality for Vue applications
 */

// Global state to prevent multiple initializations
const META_PIXEL_STATE = {
  initialized: false,
  pixelId: null,
  instance: null,
  scriptLoading: false,
  scriptLoaded: false
};

// Global flag to prevent multiple installations
let GLOBAL_META_PIXEL_INSTALLED = false;

class MetaPixelPlugin {
  constructor(pixelId, options = {}) {
    this.pixelId = pixelId;
    this.options = {
      debug: false,
      autoPageView: true,
      ...options
    };
    this.isInitialized = false;
    this.queue = [];
  }

  // Initialize the Meta Pixel using Facebook's exact pattern
  init() {
    // Check if already initialized globally
    if (META_PIXEL_STATE.initialized && META_PIXEL_STATE.pixelId === this.pixelId) {
      console.warn(`Meta Pixel with ID ${this.pixelId} already initialized globally`);
      this.isInitialized = true;
      return;
    }

    // Check if Meta Pixel is already initialized globally with different ID
    if (META_PIXEL_STATE.initialized && META_PIXEL_STATE.pixelId !== this.pixelId) {
      console.warn(`Meta Pixel already initialized with different ID: ${META_PIXEL_STATE.pixelId}`);
      this.isInitialized = true;
      return;
    }

    // Use Facebook's exact pattern: if(f.fbq)return;
    if (window.fbq) {
      console.warn('Meta Pixel already exists (window.fbq), skipping initialization');
      this.isInitialized = true;
      META_PIXEL_STATE.initialized = true;
      META_PIXEL_STATE.pixelId = this.pixelId;
      META_PIXEL_STATE.instance = this;
      return;
    }

    // Initialize using Facebook's exact pattern
    this.initializeFacebookStyle();
    
    this.isInitialized = true;
    
    // Update global state
    META_PIXEL_STATE.initialized = true;
    META_PIXEL_STATE.pixelId = this.pixelId;
    META_PIXEL_STATE.instance = this;
  }

  // Initialize using Facebook's exact pattern
  initializeFacebookStyle() {
    // Facebook's exact pattern
    if (window.fbq) return;
    
    // n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    window.fbq = function() {
      window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
    };
    
    // if(!f._fbq)f._fbq=n;
    if (!window._fbq) window._fbq = window.fbq;
    
    // n.push=n;
    window.fbq.push = window.fbq;
    
    // n.loaded=!0;
    window.fbq.loaded = true;
    
    // n.version='2.0';
    window.fbq.version = '2.0';
    
    // n.queue=[];
    window.fbq.queue = [];
    
    // t=b.createElement(e);t.async=!0;t.src=v;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    
    // s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
    
    // Initialize pixel after script loads
    script.onload = () => {
      window.fbq('init', this.pixelId);
      
      if (this.options.autoPageView) {
        window.fbq('track', 'PageView');
      }
    };
  }

  // Facebook Pixel function wrapper
  fbq(...args) {
    if (typeof window.fbq === 'undefined') {
      this.queue.push(args);
      return;
    }
    window.fbq(...args);
  }

  // Process queued events
  processQueue() {
    while (this.queue.length > 0) {
      const args = this.queue.shift();
      if (typeof window.fbq !== 'undefined') {
        window.fbq(...args);
      }
    }
  }

  // Track page view
  trackPageView() {
    this.fbq('track', 'PageView');
  }

  // Track custom event
  trackEvent(eventName, parameters = {}) {
    this.fbq('track', eventName, parameters);
  }

  // Track custom event
  trackCustomEvent(customEventName, parameters = {}) {
    this.fbq('trackCustom', customEventName, parameters);
  }

  // Track conversion
  trackConversion(value, currency = 'USD') {
    this.fbq('track', 'Purchase', {
      value: value,
      currency: currency
    });
  }

  // Track lead
  trackLead(value = 0, currency = 'USD') {
    this.fbq('track', 'Lead', {
      value: value,
      currency: currency
    });
  }

  // Track add to cart
  trackAddToCart(value, currency = 'USD', contentIds = []) {
    this.fbq('track', 'AddToCart', {
      value: value,
      currency: currency,
      content_ids: contentIds
    });
  }

  // Track view content
  trackViewContent(value = 0, currency = 'USD', contentIds = []) {
    this.fbq('track', 'ViewContent', {
      value: value,
      currency: currency,
      content_ids: contentIds
    });
  }

  // Track contact
  trackContact() {
    this.fbq('track', 'Contact');
  }

  // Track complete registration
  trackCompleteRegistration() {
    this.fbq('track', 'CompleteRegistration');
  }

  // Track start application
  trackStartApplication() {
    this.fbq('track', 'StartApplication');
  }

  // Track complete application
  trackCompleteApplication() {
    this.fbq('track', 'CompleteApplication');
  }

  // Track schedule
  trackSchedule() {
    this.fbq('track', 'Schedule');
  }

  // Track search
  trackSearch(searchString) {
    this.fbq('track', 'Search', {
      search_string: searchString
    });
  }
}

// Vue Plugin
const MetaPixel = {
  install(app, options = {}) {
    const { pixelId, ...pluginOptions } = options;
    
    if (!pixelId) {
      console.error('Meta Pixel ID is required');
      return;
    }

    // Check if plugin is already installed globally
    if (GLOBAL_META_PIXEL_INSTALLED) {
      console.warn('Meta Pixel plugin already installed globally (GLOBAL_META_PIXEL_INSTALLED)');
      return;
    }

    // Check if plugin is already installed globally (instance check)
    if (META_PIXEL_STATE.instance) {
      console.warn('Meta Pixel plugin already installed globally (META_PIXEL_STATE.instance)');
      return;
    }

    // Use Facebook's exact pattern: if(f.fbq)return;
    if (window.fbq) {
      console.warn('Meta Pixel already exists (window.fbq), skipping plugin installation');
      return;
    }

    // Mark as installed globally
    GLOBAL_META_PIXEL_INSTALLED = true;

    const metaPixel = new MetaPixelPlugin(pixelId, pluginOptions);
    
    // Store global instance
    META_PIXEL_STATE.instance = metaPixel;
    
    // Initialize pixel
    metaPixel.init();

    // Provide metaPixel instance to components
    app.provide('metaPixel', metaPixel);

    // Add global property
    app.config.globalProperties.$metaPixel = metaPixel;

    // Add router navigation tracking
    if (app.config.globalProperties.$router) {
      app.config.globalProperties.$router.afterEach(() => {
        if (pluginOptions.autoPageView !== false) {
          metaPixel.trackPageView();
        }
      });
    }
  }
};

export default MetaPixel;
